/**
 * Single source of truth for public / chrome-less routes.
 *
 * Consumed by both redirect mechanisms that wrap the route tree
 * (NewMainLayout's auth guard and AuthContext's SIGNED_OUT navigation) and by
 * NewMainLayout's chrome-less layout branch. Centralizing here keeps these in
 * sync so public pages are never silently redirected to /login or rendered with
 * the wrong chrome.
 *
 * Keep these aligned with the public routes declared in App.tsx (the routes
 * defined outside <ProtectedRoute>).
 */

// Auth / entry pages — public AND rendered without app chrome.
export const AUTH_PATHS = [
  '/login',
  '/logout',
  '/create-account',
  '/forgot-password',
  '/contact',
  '/register',
  '/registration/complete',
  '/reset-password',
  '/auth/callback',
]

// Legal / info documents — public AND chrome-less (matched by prefix).
export const LEGAL_PREFIXES = ['/ricster/', '/flowster/']

// Public pages that DO render inside the app chrome (nav bar / sidebar).
export const PUBLIC_WITH_CHROME_PATHS = ['/guest/devices']

/**
 * True for any route that does not require authentication.
 * Used by the layout/auth-context guards to avoid redirecting to /login.
 */
export function isPublicPath(pathname: string): boolean {
  return (
    AUTH_PATHS.includes(pathname) ||
    PUBLIC_WITH_CHROME_PATHS.includes(pathname) ||
    LEGAL_PREFIXES.some(prefix => pathname.startsWith(prefix))
  )
}

/**
 * True for routes rendered without the app chrome (nav bar, sidebar, tabs):
 * the auth/entry pages and standalone legal documents.
 */
export function isChromelessPath(pathname: string): boolean {
  return (
    AUTH_PATHS.includes(pathname) ||
    LEGAL_PREFIXES.some(prefix => pathname.startsWith(prefix))
  )
}
